import type { TimetableBlock } from '@classgo/shared';
import { cx } from '@classgo/ui';

import { useT } from '../../i18n';
import { addMinutes, durationOf, startTimesOf, weekdayOf, weekdaysOf } from './support';
import type { Slot, Weekday } from './types';

/**
 * A week as a table: a column per weekday, a row per start time, and in each cell the block that
 * starts there with what the view cares about (`primary` in bold, `secondary` under the hours).
 * With `onSelect`, every block is a button that opens it; with `onAdd`, every empty cell offers
 * to place a block at that day and hour. `highlighted` blocks are the ones the last refused
 * write collided with.
 */
export const TimetableGrid = ({
  label,
  blocks,
  primary,
  secondary,
  highlighted,
  onAdd,
  onSelect,
}: {
  label: string;
  blocks: readonly TimetableBlock[];
  primary: (block: TimetableBlock) => string;
  secondary: (block: TimetableBlock) => string;
  highlighted?: ReadonlySet<string>;
  onAdd?: (slot: Slot) => void;
  onSelect?: (block: TimetableBlock) => void;
}) => {
  const t = useT();
  const days = weekdaysOf(blocks);
  const times = startTimesOf(blocks);
  const dayName = (day: Weekday) => t(`timetable.day.${day}`);
  const card = (block: TimetableBlock) => (
    <>
      <span className="block font-medium text-brand-700">{primary(block)}</span>
      <span className="block text-xs text-gray-600">
        {block.startsAt}–{block.endsAt}
      </span>
      <span className="block text-xs text-gray-600">{secondary(block)}</span>
    </>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm" aria-label={label}>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            <th scope="col" className="w-20 pb-2 pr-3 font-semibold">
              {t('timetable.grid.time')}
            </th>
            {days.map((day) => (
              <th key={day} scope="col" className="pb-2 pr-3 font-semibold">
                {dayName(day)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {times.map((time) => {
            const sample = blocks.find((block) => block.startsAt === time);
            const endsAt = addMinutes(time, sample ? durationOf(sample) : 60);
            return (
              <tr key={time} className="border-t border-gray-100 align-top">
                <th scope="row" className="py-1.5 pr-3 text-left text-xs font-medium text-gray-500">
                  {time}
                </th>
                {days.map((day) => {
                  const here = blocks.filter(
                    (block) => block.weekday === day && block.startsAt === time,
                  );
                  return (
                    <td key={day} className="py-1.5 pr-3">
                      {here.length > 0 ? (
                        <div className="space-y-2">
                          {here.map((block) =>
                            onSelect ? (
                              <button
                                key={block.id}
                                type="button"
                                onClick={() => onSelect(block)}
                                aria-label={t('timetable.edit.of', {
                                  subject: block.subjectName,
                                  day: dayName(weekdayOf(block.weekday)).toLowerCase(),
                                  time: block.startsAt,
                                })}
                                className={cx(
                                  'control-touch block w-full rounded-control bg-brand-50 px-2 py-1.5 text-left transition-colors hover:bg-brand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                                  highlighted?.has(block.id) && 'ring-2 ring-danger-600',
                                )}
                              >
                                {card(block)}
                              </button>
                            ) : (
                              <div
                                key={block.id}
                                className={cx(
                                  'rounded-control bg-brand-50 px-2 py-1.5',
                                  highlighted?.has(block.id) && 'ring-2 ring-danger-600',
                                )}
                              >
                                {card(block)}
                              </div>
                            ),
                          )}
                        </div>
                      ) : onAdd ? (
                        <button
                          type="button"
                          onClick={() => onAdd({ weekday: day, startsAt: time, endsAt })}
                          aria-label={t('timetable.add.at', {
                            day: dayName(day).toLowerCase(),
                            time,
                          })}
                          className="control-touch flex w-full items-center justify-center rounded-control border border-dashed border-gray-300 px-2 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                        >
                          +
                        </button>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
